import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import Skeleton from "../../components/ui-components/Skeleton";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import InvestigationsTable from "./components/InvestigationsTable";
import NewInvestigationModal from "./components/NewInvestigationModal";
import { useInvestigationsQuery } from "./hooks";

export default function InvestigationsListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const { data: items = [], isLoading, error } = useInvestigationsQuery();
  // Open / My investigations / Closed is a row-2 tab, driven by the ?filter= query
  // param (see headerTabs.js); "open" (no param) is the default.
  const filter = searchParams.get("filter") || "open";
  const [q, setQ] = useState("");
  // InvestigateButton (inbound wiring from other pages) navigates here with a
  // pre-built Seed in router state so the guided modal opens pre-filled.
  const initialSeed = location.state?.newSeed;
  const [newOpen, setNewOpen] = useState(searchParams.get("new") === "1" || !!initialSeed);

  const filtered = useMemo(() => {
    let rows = items;
    if (filter === "open") rows = rows.filter((r) => r.status !== "closed");
    else if (filter === "closed") rows = rows.filter((r) => r.status === "closed");
    // TODO: owner filter when backend wires owner. "mine" has no owner wiring yet — show all rows for now.
    if (q) rows = rows.filter((r) => (r.title || "").toLowerCase().includes(q.toLowerCase()));
    return rows;
  }, [items, filter, q]);

  const search = q.trim();
  const matchCount = filtered.length;
  const noun = matchCount === 1 ? "investigation" : "investigations";
  const hint = isLoading
    ? "Loading…"
    : search
      ? `${matchCount} ${noun} matching "${search}"`
      : `${matchCount} ${noun}`;

  return (
    <>
      <AdministrationHeader title="Investigations" className="text-text-3" showPeriodSelector={false} />
      {/* New investigation is the page's primary action, in the row-1 header cluster. */}
      <HeaderPortal slot="actions">
        <button type="button" onClick={() => setNewOpen(true)} className={primaryButtonCls}>
          <FontAwesomeIcon icon={icon({ name: "plus", style: "solid" })} className="mr-1.5 text-[12px]" />
          New investigation
        </button>
      </HeaderPortal>
      <div className="w-full p-8">
        <TableCard
          subtitle={hint}
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Search investigations…"
          error={error}
          errorMessage="Error loading investigations"
          onRetry={() => qc.invalidateQueries(["investigations"])}
        >
          {/* No TableCard spinner here — the global top-bar loading indicator (App.js
              useIsFetching) already covers loading; show skeleton rows instead of a
              second spinner or an empty-state flash. */}
          {isLoading ? (
            <div className="space-y-3 py-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} width="100%" height={52} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={icon({ name: "magnifying-glass-chart", style: "light" })} title="No investigations yet"
              message="Start one from a budget breach, a spend change, or the biggest mover."
              action={{ label: "New investigation", onClick: () => setNewOpen(true), icon: icon({ name: "plus" }) }} />
          ) : filtered.length === 0 ? (
            <EmptyState icon={icon({ name: "magnifying-glass" })} title="No matching investigations"
              message="Try a different filter or search." action={{ label: "Clear search", onClick: () => setQ(""), variant: "secondary" }} />
          ) : (
            <InvestigationsTable data={filtered} onRowClick={(id) => navigate(`/investigations/${id}`)} />
          )}
        </TableCard>
      </div>
      <NewInvestigationModal open={newOpen} onClose={() => setNewOpen(false)} initialSeed={initialSeed} />
    </>
  );
}
